/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';

import { getContractorTrade, getPartCostValue } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { COLORS, FONT } from '@/Client/Partials/CloseoutReviewFlow/closeoutTokens';
import { formatUsd, getHumanReadablePartialId } from '@/Client/Presenters';
import type { ScanGroupOptimized, ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';
import { CloseIcon, EditIcon, LeftIcon, RightIcon } from '@kaiizen/kai-shiki/react';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { useProgressAlt } from './progressAltContext';
import { getCellPhotos } from './progressAltCursorLib';
import { PHOTO_VIEW_DETAILS_WIDTH_PX } from './progressAltMetrics';
import { ProgressAltScopeOutline } from './ProgressAltScopeOutline';
import { getProgressAltScopeOptions } from './progressAltScopeOutlineLib';
import { FOCUS_RING_STYLE } from './progressAltStyles';

const PANE_STYLE = css`
  label: PROGRESS_ALT_PHOTO_DETAILS_STYLE;
  box-sizing: border-box;
  flex: 0 0 ${PHOTO_VIEW_DETAILS_WIDTH_PX}px;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-left: 1px solid ${COLORS.stroke};
  background: ${COLORS.white};
  color: ${COLORS.ink};
`;

/**
 * Says which kind of scope is on screen and carries the moves along its row. The kind is stated
 * outright so the rows beneath it need no heading of their own — a reader who knows they are looking
 * at a task does not need "TASK INFO" written over its particulars.
 */
const TITLEBAR_STYLE = css`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 10px 10px 18px;
  border-bottom: 1px solid ${COLORS.stroke};
  background: ${COLORS.sunken};
`;

const KIND_STYLE = css`
  flex: 1;
  min-width: 0;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.midtoneFaded};
`;

const NAV_BUTTON_STYLE = css`
  box-sizing: border-box;
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid ${COLORS.strokeFaded};
  border-radius: 0;
  background: ${COLORS.white};
  color: ${COLORS.ink};
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${COLORS.midtone};
  }

  &:disabled {
    cursor: default;
    opacity: 0.3;
  }

  ${FOCUS_RING_STYLE}
`;

/** Set apart from the row moves by a gap and a darker fill: leaving is not another step sideways. */
const CLOSE_BUTTON_STYLE = css`
  margin-left: 6px;
  border-color: ${COLORS.ink};
  background: ${COLORS.ink};
  color: ${COLORS.white};

  &:hover:not(:disabled) {
    border-color: ${COLORS.midtone};
    background: ${COLORS.midtone};
  }
`;

const SCROLL_STYLE = css`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const HEADING_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 20px 22px;
  border-bottom: 1px solid ${COLORS.strokeFaded};
`;

const TITLE_STYLE = css`
  margin: 0;
  font-family: ${FONT.display};
  font-size: ${pxToRem(22)};
  font-weight: 700;
  line-height: 1.227;
  color: ${COLORS.ink};
`;

const SUBTITLE_STYLE = css`
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${COLORS.midtoneFaded};
`;

const ROWS_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 11px;
  padding: 18px 22px 22px;
  border-bottom: 1px solid ${COLORS.strokeFaded};
`;

const ROW_STYLE = css`
  display: grid;
  grid-template-columns: minmax(84px, 0.66fr) minmax(0, 1.34fr);
  align-items: baseline;
  gap: 14px;
  min-width: 0;
  font-size: ${pxToRem(14)};
`;

const ROW_LABEL_STYLE = css`
  color: ${COLORS.midtoneFaded};
`;

const ROW_VALUE_STYLE = css`
  min-width: 0;
  overflow-wrap: anywhere;
  font-weight: 500;
  color: ${COLORS.ink};
`;

const NOTES_STYLE = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px 22px;
`;

const NOTES_TITLE_STYLE = css`
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.midtoneFaded};
`;

const NOTES_QUOTE_STYLE = css`
  margin: 0;
  padding: 14px 16px;
  border-left: 3px solid ${COLORS.stroke};
  background: ${COLORS.wash};
  font-size: ${pxToRem(14)};
  line-height: 1.55;
  color: ${COLORS.ink};
`;

const FOOTER_STYLE = css`
  flex: 0 0 auto;
  display: flex;
  padding: 12px 14px 14px;
  border-top: 1px solid ${COLORS.strokeFaded};
  background: ${COLORS.white};
  box-shadow: 0 -6px 16px rgba(29, 29, 29, 0.05);
`;

/**
 * The one way out to changing anything. Nothing on this pane edits the scope — the view is for reading
 * photos — so the single action is a trip to the editor with this scope already selected.
 */
const EDIT_BUTTON_STYLE = css`
  flex: 1;
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid ${COLORS.stroke};
  border-radius: 0;
  background: ${COLORS.white};
  color: ${COLORS.ink};
  font-family: ${FONT.mono};
  font-size: ${pxToRem(11)};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover {
    border-color: ${COLORS.ink};
    background: ${COLORS.sunken};
  }

  ${FOCUS_RING_STYLE}
`;

/**
 * The scope's particulars, and only what is not already on screen: a task's group is the heading's own
 * subtitle, so it does not earn a row as well. Neither list needs a heading saying which kind it
 * describes — the pane's titlebar has already said so.
 */
const getDetailRows = (
  group: ScanGroupOptimized | undefined,
  task: ScanTaskOptimized | undefined,
  photoCount: number,
): readonly { label: string; value?: string }[] => [
  ...(task
    ? [
      { label: 'Task ID', value: getHumanReadablePartialId(task.id) },
      { label: 'Trade', value: getContractorTrade(task) },
    ]
    : [
      { label: 'Group ID', value: getHumanReadablePartialId(group?.id) },
      { label: 'Tasks', value: `${group?.tasks?.length ?? 0}` },
    ]),
  { label: 'Cost', value: formatUsd(getPartCostValue(task ?? group), false, true) },
  { label: 'Photos', value: `${photoCount}` },
];

/**
 * The photo view's right-hand pane: which scope the photo belongs to, its particulars, and the moves
 * along the row it sits in.
 *
 * Reads the scope off the context rather than being handed it, so an edit to a task's cost, notes or
 * line items reaches this pane without the stage in between having to know it did.
 */
export const ProgressAltPhotoDetails = () => {
  const { timeline, cursor, photoScope, photoNeighbors, movePhotoCursor, closePhotoView, editScope } = useProgressAlt();
  const column = photoScope?.column;
  if (!timeline || !cursor || !column) return null;

  const { group, task } = photoScope;
  const kind = column.taskId ? 'Task' : 'Group';
  const photoCount = timeline.rows.reduce(
    (total, _row, rowIndex) => total + getCellPhotos(timeline, rowIndex, cursor.columnIndex).length,
    0,
  );
  const notes = task?.template?.description?.trim() || undefined;

  return (
    <aside css={PANE_STYLE} data-testid='progress-alt-photo-details'>
      <div css={TITLEBAR_STYLE}>
        <span css={KIND_STYLE} data-testid='progress-alt-photo-kind'>{kind}</span>
        <button
          type='button'
          css={NAV_BUTTON_STYLE}
          aria-label='Previous scope'
          disabled={!photoNeighbors.left.length}
          onClick={() => movePhotoCursor('left')}
        >
          <LeftIcon size={15} color='currentColor' aria-hidden='true' />
        </button>
        <button
          type='button'
          css={NAV_BUTTON_STYLE}
          aria-label='Next scope'
          disabled={!photoNeighbors.right.length}
          onClick={() => movePhotoCursor('right')}
        >
          <RightIcon size={15} color='currentColor' aria-hidden='true' />
        </button>
        <button
          type='button'
          css={[NAV_BUTTON_STYLE, CLOSE_BUTTON_STYLE]}
          aria-label='Close photo view'
          onClick={closePhotoView}
        >
          <CloseIcon size={14} color='currentColor' aria-hidden='true' />
        </button>
      </div>

      <div css={SCROLL_STYLE}>
        {/* The names the grid's header bar wore, so the scope stays recognisable. */}
        <header css={HEADING_STYLE}>
          <h2 css={TITLE_STYLE}>{column.title}</h2>
          <span css={SUBTITLE_STYLE}>{column.subtitle}</span>
        </header>

        <div css={ROWS_STYLE} data-testid='progress-alt-photo-detail-rows'>
          {getDetailRows(group, task, photoCount).map(row => (
            <div key={row.label} css={ROW_STYLE}>
              <span css={ROW_LABEL_STYLE}>{row.label}</span>
              <span css={ROW_VALUE_STYLE}>{row.value ?? '—'}</span>
            </div>
          ))}
        </div>

        {notes
          ? (
            <section css={NOTES_STYLE}>
              <h3 css={NOTES_TITLE_STYLE}>Notes</h3>
              <p css={NOTES_QUOTE_STYLE}>{notes}</p>
            </section>
          )
          : null}

        {/* Last because it is the longest: what the task consists of, after what it is and what it says. */}
        <ProgressAltScopeOutline options={getProgressAltScopeOptions(task)} />
      </div>

      <footer css={FOOTER_STYLE}>
        <button type='button' css={EDIT_BUTTON_STYLE} onClick={editScope}>
          <EditIcon size={15} color='currentColor' aria-hidden='true' />
          <span>{`Edit ${kind.toLowerCase()}`}</span>
        </button>
      </footer>
    </aside>
  );
};
